import React from 'react';
import styled from 'styled-components';
import { Star } from 'lucide-react';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';

/* Medidas tiradas do layout de referência em 1600×828. */

const iconProps = {
    width: 25, height: 25, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
    strokeWidth: 2.3, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': 'true',
};

const CalendarIcon = () => (
    <svg {...iconProps}>
        <rect x='3.5' y='5' width='17' height='15.5' rx='2.5' />
        <path d='M8 3v4M16 3v4M3.5 10h17M9.5 15.5h2.5M14.5 14.2v2.6' />
    </svg>
);

const PinIcon = () => (
    <svg {...iconProps}>
        <path d='M12 21.5s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11z' />
        <circle cx='12' cy='10.3' r='2.3' fill='currentColor' stroke='none' />
    </svg>
);

const TicketIcon = () => (
    <svg {...iconProps}>
        <rect x='3.8' y='6.8' width='16.4' height='10.4' rx='1.6' transform='rotate(-38 12 12)' />
        <circle cx='19.4' cy='17.2' r='.7' fill='currentColor' stroke='none' />
    </svg>
);

const StarIcon = () => <Star size={25} strokeWidth={2.3} aria-hidden='true' />;

const features = [
    { icon: CalendarIcon, title: 'Eventos', description: 'de todos os tipos' },
    { icon: PinIcon, title: 'Mapa interativo', description: 'com o que tá rolando' },
    { icon: TicketIcon, title: 'Ingressos e produtos', description: 'em um só checkout' },
    { icon: StarIcon, title: 'Experiências', description: 'para todos os gostos' },
];

function SignInHero() {
    return (
        <>
            <Hero>
                <Headline>
                    Descubra eventos,<br />
                    experiências e tudo o que<br />
                    <Highlight>tá rolando na sua região.</Highlight>
                </Headline>

                <Lead>
                    Shows, festas, bares, experiências e muito mais.<br />
                    Tudo em um só lugar, do seu jeito.
                </Lead>

                <Features>
                    <Curve width='1100' height='900' viewBox='0 0 1100 900' aria-hidden='true'>
                        <path d='M -40 -29.1 L 0 -11.5 C 13.3 -5.7, 28.3 0.8, 40 5.8 C 51.7 10.8, 53.3 14.3, 70 18.7 C 86.7 23.1, 118.3 29, 140 32 C 161.7 35, 182 34.8, 200 36.5 C 218 38.2, 233 39.8, 248 42 C 263 44.2, 274.7 45.5, 290 49.5 C 305.3 53.5, 325 61.2, 340 66.2 C 355 71.2, 366.7 74, 380 79.5 C 393.3 85, 406.7 90.6, 420 98.9 C 433.3 107.1, 446.7 117.9, 460 129 C 473.3 140.1, 486.7 152.5, 500 165.5 C 513.3 178.5, 526.7 192.8, 540 207 C 553.3 221.2, 566.7 236.6, 580 250.6 C 593.3 264.6, 606.7 277.6, 620 291 L 1000 671' />
                    </Curve>
                    {features.map(({ icon: FeatureIcon, title, description }) => (
                        <Feature key={title}>
                            <FeatureIconCircle>
                                <FeatureIcon />
                            </FeatureIconCircle>
                            <FeatureTitle>{title}</FeatureTitle>
                            <FeatureDescription>{description}</FeatureDescription>
                        </Feature>
                    ))}
                </Features>
            </Hero>

            {/* Coordenadas do layout de referência: a área cobre x 0–260 e os
                140px de baixo da tela. */}
            <Signature viewBox='0 0 260 140' aria-hidden='true'>
                <path d='M 61 40.5 L 68 39' strokeWidth='2.4' />
                <text x='80' y='51' transform='rotate(-14 80 51) skewX(-10)'>Viva o agora</text>
                <path d='M 101 58 Q 128 44 186 38' strokeWidth='3.4' />
                <path d='M 194.5 34 L 198 28 M 196.5 40 L 203 42 M 192.5 43.5 L 197.5 48' strokeWidth='2.2' />
            </Signature>
        </>
    );
}

const desktopOnly = `
    display: none;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: block;
    }
`;

/* Presa ao bloco de destaques (e não à tela) para cruzar os ícones sempre no
   mesmo ponto: a origem fica 72px à esquerda do primeiro ícone, na borda da
   página. Fica acima do fundo translúcido dos círculos e abaixo dos ícones e
   textos, como no layout. */
const Curve = styled.svg`
    position: absolute;
    z-index: 1;
    top: 0;
    left: -72px;
    overflow: visible;
    pointer-events: none;

    & > path {
        fill: none;
        stroke: rgba(186, 156, 255, .28);
        stroke-width: 1;
    }
`;

const Hero = styled.div`
    display: none;

    @media screen and (min-width: ${breakpoints.lg}) {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: calc(max(34.5625%, 460px) + 40px);
        padding-left: 72px;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding-top: 60px;
        color: ${colors.neutralLight};
    }
`;

const Headline = styled.h1`
    font-size: 42.5px;
    font-weight: 600;
    line-height: 48.5px;
    letter-spacing: 0;
    color: ${colors.neutralLight};
    margin: 0;
    white-space: nowrap;
`;

const Highlight = styled.span`
    background: linear-gradient(90deg, #7F3FFC 0%, #A865FD 55%, #D38AFD 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
`;

const Lead = styled.p`
    margin-top: 23px;
    font-size: 17.15px;
    line-height: 24px;
    color: rgba(255, 255, 255, .9);
`;

const Features = styled.ul`
    position: relative;
    list-style: none;
    margin: 62px 0 0;
    padding: 0;
    display: grid;
    grid-template-columns: 152px 168px 174px auto;
`;

const Feature = styled.li`
    position: relative;
    display: flex;
    flex-direction: column;
`;

const FeatureIconCircle = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: rgba(134, 129, 235, 0.14);
    box-shadow: inset 0 0 3px 1px rgba(12, 10, 28, .3);
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
    color: #8F63EA;
    margin-bottom: 6px;

    & > svg {
        position: relative;
        z-index: 2;
        filter: drop-shadow(0 0 3px rgba(143, 99, 234, .55));
    }
`;

const FeatureTitle = styled.span`
    position: relative;
    z-index: 2;
    padding-left: 2px;
    font-size: 12.4px;
    line-height: 17px;
    font-weight: 500;
    color: ${colors.neutralLight};
`;

const FeatureDescription = styled.span`
    position: relative;
    z-index: 2;
    padding-left: 1px;
    font-size: 10.7px;
    line-height: 15px;
    color: rgba(255, 255, 255, .82);
`;

const Signature = styled.svg`
    ${desktopOnly};
    position: absolute;
    left: 0;
    bottom: 0;
    width: 260px;
    height: 140px;
    overflow: visible;
    color: #8553EA;

    & text {
        font-family: 'Caveat', cursive;
        font-size: 27.6px;
        font-weight: 700;
        fill: currentColor;
        stroke: currentColor;
        stroke-width: .5;
    }

    & path {
        fill: none;
        stroke: currentColor;
        stroke-linecap: round;
    }
`;

export default SignInHero;
